import { zodResolver } from '@hookform/resolvers/zod';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { AccessibilityInfo, Platform } from 'react-native';

import { SurveyCheckboxQuestion } from './SurveyCheckboxQuestion';
import { SurveyChoiceQuestion } from './SurveyChoiceQuestion';
import { SurveyCommentQuestion } from './SurveyCommentQuestion';
import { SurveyDropdownQuestion } from './SurveyDropdownQuestion';
import { SurveyHtmlBlock } from './SurveyHtmlBlock';
import { SurveyInquiryQuestion } from './SurveyInquiryQuestion';
import { SurveyTextQuestion } from './SurveyTextQuestion';
import { useSurveyDraft } from '../../hooks/useSurveyDraft';
import { stableChoiceOrder } from '../../model/shuffleChoices';
import {
  buildSurveyAnswers,
  buildSurveyFormSchema,
  getHiddenSurveyElementNames,
  getSurveyConsentFieldName,
  getSurveyOtherFieldName,
  hasOtherItem,
  isConditionallyVisible,
  isPrimumInquiryElement,
  SUPPORTED_SURVEY_ELEMENT_TYPES,
  SURVEY_DISPLAY_ELEMENT_TYPES,
  withInquiryConsentRule,
  withOtherItemRule,
  withSelectionCountRule,
  withVisibilityRules,
  type SurveyElement,
  type SurveyPage,
} from '../../model/surveySchema';
import type { SurveyType } from '../../model/surveyType';
import {
  getUnsupportedVisibilityRules,
  getVisibilityDependencyNames,
} from '../../model/surveyVisibility';
import { selectRestorableValues } from '../../state/surveyDrafts';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { LoaderButton } from '@/components/ui/loader-button';
import { Progress, ProgressFilledTrack } from '@/components/ui/progress';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { trackSurveyPageViewed } from '@/util/analyticsEvents';
import { Sentry } from '@/util/sentry';

function isAnswerableElementType(type: string) {
  return SUPPORTED_SURVEY_ELEMENT_TYPES.includes(
    type as (typeof SUPPORTED_SURVEY_ELEMENT_TYPES)[number]
  );
}

function isRecognizedElementType(type: string) {
  return (
    isAnswerableElementType(type) ||
    SURVEY_DISPLAY_ELEMENT_TYPES.includes(type as (typeof SURVEY_DISPLAY_ELEMENT_TYPES)[number])
  );
}

/**
 * Every form field an element owns: its answer, plus the companion fields that
 * ride alongside it in flat form state.
 *
 * The companions have to travel with the question wherever it is validated. A
 * page-at-a-time `trigger` names fields explicitly, so a companion left out is
 * a field whose error is computed and then never shown — a respondent blocked
 * at Submit by a question they already answered pages ago.
 */
function getElementFieldNames(element: SurveyElement) {
  return [
    element.name,
    ...(isPrimumInquiryElement(element) ? [getSurveyConsentFieldName(element.name)] : []),
    ...(hasOtherItem(element) ? [getSurveyOtherFieldName(element.name)] : []),
  ];
}

export interface SurveyFormProps {
  surveyId: string;
  surveyName: string;
  pages: SurveyPage[];
  onSubmit: (answers: Record<string, unknown>) => Promise<void> | void;
  isSubmitting: boolean;
  /**
   * Survey group id. Supplying it turns on draft persistence; omitting it
   * renders a form that keeps nothing, which is what a preview or a test
   * wants.
   */
  surveyGroupId?: string;
  /** Scopes a draft to its author. Absent for a guest on the public screen. */
  userId?: string;
  /**
   * Fired when the stepper moves, and whenever validation sends the respondent
   * back to the top of a page. Scrolling belongs to the screen, which owns the
   * scroll view the form is rendered into.
   */
  onScrollToTop?: () => void;
  /**
   * The survey's `SurveyType` from the API. A poll is short enough — usually
   * one or two questions — that draft persistence and required-field markers
   * are implied rather than worth stating, so `'poll'` suppresses both.
   */
  surveyType?: SurveyType;
}

/**
 * Renders a survey's questions and handles submission. Dispatches each
 * answerable element to a question renderer by `type` and each display
 * element (`html`) to `SurveyHtmlBlock`. An element with an unrecognized
 * type is dropped from both the validation schema and the rendered form
 * (see `buildSurveyFormSchema`), so a survey schema using a type this client
 * doesn't know yet degrades to "missing question" rather than a crash.
 *
 * A `comment` element carrying an enabled Primum inquiry marker renders with an
 * extra acknowledgement field. That field is form state only — it is folded
 * into its question's answer here rather than sent as a key of its own, so what
 * reaches `onSubmit` is the `{ [element.name]: answer }` map the survey API
 * expects and both screens forward it to the API verbatim.
 *
 * An element carrying a `visibleIf` condition is shown only while that condition
 * holds, and hidden means hidden all the way through: it is not validated, its
 * own `isRequired` does not apply, and its answer never reaches `onSubmit`. Its
 * value is deliberately kept in form state rather than cleared, so that changing
 * an answer back restores what the respondent had already typed — nothing
 * depends on that value being absent, because both validation and the payload
 * resolve visibility themselves rather than inferring it from emptiness.
 *
 * Pages are laid out one at a time, but they are *only* layout here: a page's
 * own condition was folded into its elements at parse time, so everything below
 * — validation, the payload, the draft — still works on one flat element list
 * and knows nothing about pages. A survey with a single page renders exactly as
 * it did before pages existed, with no stepper chrome.
 *
 * Given a `surveyGroupId` on a non-poll survey, answers are also saved to
 * device storage as they are typed and restored on the next visit, so leaving
 * a survey part-finished is recoverable. A poll gets neither the persistence
 * nor its notice — short enough that losing progress is not worth the promise.
 * A currently-hidden question's answer is saved and restored along
 * with the rest, for the same reason the form keeps it in state: coming back to
 * a survey and flipping a condition back should return what was already typed,
 * and it is the payload builder — not the draft — that decides what a hidden
 * question contributes at submit. The page they had reached is restored too,
 * falling back to the first visible page whenever that name no longer resolves
 * — a renamed page, or one their own answers have since made unreachable.
 * Discarding a saved draft
 * once its response is accepted is the screen's job, not the form's: the form
 * cannot tell a successful submit from a failed one, because both screens report
 * their own errors and resolve `onSubmit` either way.
 */
export function SurveyForm({
  surveyId,
  surveyName,
  pages,
  onSubmit,
  isSubmitting,
  surveyGroupId,
  userId,
  onScrollToTop,
  surveyType,
}: SurveyFormProps) {
  const isPoll = surveyType === 'poll';

  const elements = useMemo(() => pages.flatMap((page) => page.elements), [pages]);

  /**
   * Shuffled option orders live here, above the pages, so a question that
   * remounts when the respondent pages back, or a refetch that hands over a new
   * schema object, keeps the order the respondent already saw.
   */
  const choiceOrdersRef = useRef(new Map<string, string[]>());

  const displayChoicesFor = (element: SurveyElement) => {
    if (element.choicesOrder !== 'random') {
      return undefined;
    }

    const order = stableChoiceOrder(
      choiceOrdersRef.current.get(element.name),
      element.choices ?? []
    );

    choiceOrdersRef.current.set(element.name, order);

    return order;
  };

  const renderableElements = useMemo(
    () => elements.filter((element) => isRecognizedElementType(element.type)),
    [elements]
  );

  const answerableElements = useMemo(
    () => renderableElements.filter((element) => isAnswerableElementType(element.type)),
    [renderableElements]
  );

  const formSchema = useMemo(
    () =>
      withSelectionCountRule(
        withOtherItemRule(
          withInquiryConsentRule(
            withVisibilityRules(buildSurveyFormSchema(elements), elements),
            elements
          ),
          elements
        ),
        elements
      ),
    [elements]
  );

  const visibilityDependencyNames = useMemo(
    () => getVisibilityDependencyNames(elements),
    [elements]
  );

  /**
   * Covers every answerable element, conditional ones included, and every
   * companion field. React Hook Form reads an object of defaults exactly once,
   * so narrowing this to the currently visible questions would not track
   * visibility — it would just leave the hidden ones undefined for the life of
   * the form. A companion missing from here is also dropped when a saved draft
   * is restored, since restoring filters to known field names.
   */
  const defaultValues = useMemo(
    () =>
      Object.fromEntries(
        answerableElements.flatMap((element) => [
          [element.name, element.type === 'checkbox' ? [] : ''],
          ...(isPrimumInquiryElement(element)
            ? [[getSurveyConsentFieldName(element.name), false]]
            : []),
          ...(hasOtherItem(element) ? [[getSurveyOtherFieldName(element.name), '']] : []),
        ])
      ),
    [answerableElements]
  );

  useEffect(() => {
    elements
      .filter((element) => !isRecognizedElementType(element.type))
      .forEach((element) => {
        Sentry.addBreadcrumb({
          category: 'survey',
          level: 'warning',
          message: 'Skipped survey element with an unrecognized type',
          data: { surveyId, elementName: element.name, elementType: element.type },
        });
      });
  }, [elements, surveyId]);

  useEffect(() => {
    getUnsupportedVisibilityRules(elements).forEach((element) => {
      Sentry.addBreadcrumb({
        category: 'survey',
        level: 'warning',
        message: 'Showed a survey element whose visibility condition is unsupported',
        data: { surveyId, elementName: element.name, visibleIf: element.visibleIf },
      });
    });
  }, [elements, surveyId]);

  /**
   * Reported separately from the element-level rules above because a page
   * condition that cannot be parsed is not folded into its elements at all, so
   * it leaves no element-level trace — the page simply stays visible and the
   * survey looks like it was authored without a condition.
   */
  useEffect(() => {
    getUnsupportedVisibilityRules(pages).forEach((page) => {
      Sentry.addBreadcrumb({
        category: 'survey',
        level: 'warning',
        message: 'Showed a survey page whose visibility condition is unsupported',
        data: { surveyId, pageName: page.name, visibleIf: page.visibleIf },
      });
    });
  }, [pages, surveyId]);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  const [currentPageName, setCurrentPageName] = useState<string | undefined>(undefined);

  /**
   * A poll never shows the "saved on this device" notice (see below), so
   * persisting a draft anyway would keep it on disk with no promise made
   * about it — the opposite of the guarantee that notice exists to state.
   */
  const { restoredDraft, isDraftLoaded, saveDraft } = useSurveyDraft({
    userId,
    surveyGroupId: isPoll ? undefined : surveyGroupId,
    surveyDefinitionId: surveyId,
    isPaused: isSubmitting,
  });

  const hasRestoredRef = useRef(false);

  /**
   * Read at flush time rather than closed over, so the draft records the page
   * the respondent was actually on. Threading it through the subscription
   * instead would re-subscribe on every page change and drop whatever that
   * subscription had already queued.
   */
  const currentPageNameRef = useRef<string | undefined>(undefined);

  /**
   * Seeds the form from the saved draft, once. A user who started typing
   * before the read came back is left alone: their live answers are newer than
   * anything on disk, and overwriting them would be the one thing worse than
   * losing the draft.
   */
  useEffect(() => {
    if (!isDraftLoaded || hasRestoredRef.current) {
      return;
    }

    hasRestoredRef.current = true;

    if (!restoredDraft || isDirty) {
      return;
    }

    reset({ ...defaultValues, ...selectRestorableValues(restoredDraft.values, defaultValues) });

    // A page that no longer exists, or that this respondent's answers have made
    // unreachable, needs no special case: an unknown name falls back to the
    // first visible page exactly as a fresh visit does.
    setCurrentPageName(restoredDraft.pageName);
  }, [isDraftLoaded, restoredDraft, isDirty, reset, defaultValues]);

  /**
   * Withheld until the draft has been read, so the empty form the user is
   * shown while that read is in flight cannot overwrite the answers it is
   * about to restore.
   */
  useEffect(() => {
    if (!isDraftLoaded) {
      return;
    }

    const subscription = watch((values) =>
      saveDraft(values as Record<string, unknown>, currentPageNameRef.current)
    );

    return () => subscription.unsubscribe();
  }, [isDraftLoaded, watch, saveDraft]);

  /**
   * Subscribed by name rather than to the whole form, so a survey with no
   * conditions re-renders exactly as often as it did before they existed. The
   * names come from the same parser that decides visibility, so the subscription
   * cannot fall out of step with what is actually read. `exact` matters because
   * the default subscription matches by prefix, which would make one question's
   * name capture another that merely starts with it.
   */
  const watchedValues = useWatch({ control, name: visibilityDependencyNames, exact: true });

  const hiddenElementNames = useMemo(
    () =>
      getHiddenSurveyElementNames(
        elements,
        Object.fromEntries(
          visibilityDependencyNames.map((name, index) => [name, watchedValues[index]])
        )
      ),
    [elements, visibilityDependencyNames, watchedValues]
  );

  const conditionalElementNames = useMemo(
    () => new Set(elements.filter(isConditionallyVisible).map((element) => element.name)),
    [elements]
  );

  /**
   * The pages a respondent can currently reach.
   *
   * A page is skipped when nothing on it is showing, which covers both a page
   * whose own condition is false — folded into every one of its elements at
   * parse time — and one whose questions have each been hidden individually.
   * Deriving it from the hidden set rather than re-evaluating the page's
   * condition is what keeps pages from becoming a second visibility mechanism
   * that could disagree with the first.
   */
  const visiblePages = useMemo(
    () =>
      pages.filter((page) =>
        page.elements.some(
          (element) =>
            isRecognizedElementType(element.type) && !hiddenElementNames.has(element.name)
        )
      ),
    [pages, hiddenElementNames]
  );

  /**
   * Position is held by page name, not index. The visible-page set changes as
   * answers change, so an index quietly comes to point at a different page —
   * answering a screening question would advance or rewind the respondent
   * without either of them touching a button.
   */
  const currentPageIndex = visiblePages.findIndex((page) => page.name === currentPageName);

  const currentPage = currentPageIndex === -1 ? visiblePages[0] : visiblePages[currentPageIndex];

  const visiblePageNumber = currentPageIndex === -1 ? 0 : currentPageIndex;

  const isLastPage = visiblePageNumber === visiblePages.length - 1;

  currentPageNameRef.current = currentPage?.name;

  const goToPage = useCallback(
    (pageName: string) => {
      setCurrentPageName(pageName);

      onScrollToTop?.();
    },
    [onScrollToTop]
  );

  /**
   * iOS VoiceOver ignores the live regions used elsewhere in this form, and a
   * page change replaces everything on screen, so without this a respondent
   * there is moved to a new set of questions with no indication it happened.
   */
  const announcedPageRef = useRef<string | undefined>(undefined);

  /**
   * Set whenever a page change is announced, so the reveal effect below stays
   * quiet for the same commit. Moving to a new page reveals every conditional
   * question on it at once, and reading those titles after the page
   * announcement would bury it under a list the respondent has not reached yet.
   */
  const announcedPageThisCommitRef = useRef(false);

  useEffect(() => {
    announcedPageThisCommitRef.current = false;

    if (!currentPage || announcedPageRef.current === currentPage.name) {
      return;
    }

    const isFirstRender = announcedPageRef.current === undefined;

    announcedPageRef.current = currentPage.name;

    announcedPageThisCommitRef.current = true;

    if (isFirstRender || visiblePages.length < 2 || Platform.OS !== 'ios') {
      return;
    }

    const position = `Page ${visiblePageNumber + 1} of ${visiblePages.length}`;

    AccessibilityInfo.announceForAccessibility(
      currentPage.title ? `${currentPage.title}. ${position}.` : `${position}.`
    );
  }, [currentPage, visiblePageNumber, visiblePages.length]);

  /**
   * The live region wrapping each conditional question covers Android and web,
   * but iOS VoiceOver ignores it, so a respondent there is silently handed a
   * question that can block their submit. This is the same remedy the reference
   * search blurb uses for the same gap, and it stays necessary on a single-page
   * survey, where no page change ever fires the announcement above.
   */
  const previouslyHiddenNamesRef = useRef(hiddenElementNames);

  useEffect(() => {
    const revealedTitles = renderableElements
      .filter(
        (element) =>
          conditionalElementNames.has(element.name) &&
          previouslyHiddenNamesRef.current.has(element.name) &&
          !hiddenElementNames.has(element.name)
      )
      .map((element) => element.title)
      .filter((title): title is string => !!title);

    previouslyHiddenNamesRef.current = hiddenElementNames;

    if (
      Platform.OS !== 'ios' ||
      revealedTitles.length === 0 ||
      announcedPageThisCommitRef.current
    ) {
      return;
    }

    AccessibilityInfo.announceForAccessibility(
      `${revealedTitles.join('. ')}. New question${revealedTitles.length > 1 ? 's' : ''} added.`
    );
  }, [hiddenElementNames, renderableElements, conditionalElementNames]);

  const currentPageFieldNames = useMemo(
    () =>
      (currentPage?.elements ?? [])
        .filter((element) => isAnswerableElementType(element.type))
        .flatMap(getElementFieldNames),
    [currentPage]
  );

  /**
   * Pages the respondent has already tried to leave.
   *
   * React Hook Form re-validates on change only after a `handleSubmit`, and the
   * stepper blocks with `trigger` instead — so without this a corrected answer
   * leaves its error and the summary standing until Next is pressed again,
   * which reads as the fix not having worked. Validation stays quiet until the
   * first blocked attempt, so nobody is told they are wrong before they have
   * finished answering.
   */
  const [attemptedPageNames, setAttemptedPageNames] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!currentPage || !attemptedPageNames.has(currentPage.name)) {
      return;
    }

    const subscription = watch(() => {
      trigger(currentPageFieldNames).catch(() => undefined);
    });

    return () => subscription.unsubscribe();
  }, [currentPage, attemptedPageNames, watch, trigger, currentPageFieldNames]);

  const goToNextPage = useCallback(async () => {
    if (!currentPage) {
      return;
    }

    const fieldNames = currentPageFieldNames;

    /**
     * The resolver validates the whole survey on every pass, but React Hook
     * Form keeps errors only for the names given here. That is what lets Next
     * block on this page alone while a later page's unanswered required
     * question stays quiet until the respondent reaches it.
     */
    if (fieldNames.length > 0 && !(await trigger(fieldNames))) {
      setAttemptedPageNames((attempted) => new Set(attempted).add(currentPage.name));

      // Deliberately no scroll: the summary renders beside the button that just
      // refused, so the respondent is already looking at it. Scrolling to the
      // top here would carry them away from the one thing that explains the
      // refusal.
      return;
    }

    const nextPage = visiblePages[visiblePageNumber + 1];

    if (nextPage) {
      trackSurveyPageViewed({
        surveyId,
        surveyName,
        surveyGroupId,
        direction: 'next',
        fromPageName: currentPage.name,
        fromPageIndex: visiblePageNumber,
        toPageName: nextPage.name,
        toPageIndex: visiblePageNumber + 1,
        toPageTitle: nextPage.title,
        totalPages: visiblePages.length,
      });

      goToPage(nextPage.name);
    }
  }, [
    currentPage,
    currentPageFieldNames,
    trigger,
    visiblePages,
    visiblePageNumber,
    goToPage,
    onScrollToTop,
    surveyId,
    surveyName,
    surveyGroupId,
  ]);

  const goToPreviousPage = useCallback(() => {
    const previousPage = visiblePages[visiblePageNumber - 1];

    if (previousPage && currentPage) {
      trackSurveyPageViewed({
        surveyId,
        surveyName,
        surveyGroupId,
        direction: 'previous',
        fromPageName: currentPage.name,
        fromPageIndex: visiblePageNumber,
        toPageName: previousPage.name,
        toPageIndex: visiblePageNumber - 1,
        toPageTitle: previousPage.title,
        totalPages: visiblePages.length,
      });

      goToPage(previousPage.name);
    }
  }, [visiblePages, visiblePageNumber, goToPage, currentPage, surveyId, surveyName, surveyGroupId]);

  /**
   * Closed synchronously on the first accepted submit, because the button's
   * own disabled state arrives a render later and two presses landing in the
   * same frame both read it as still open. That gap is only ever a few
   * milliseconds on an idle device, but it widens to however long the main
   * thread is busy, so a plain double-tap reaches it on slow hardware.
   *
   * A survey carrying a Primum inquiry question turns each accepted submit
   * into a real inquiry routed to an expert, so a duplicate is not merely a
   * redundant row — hence a ref rather than leaning on the disabled state.
   */
  const isSubmittingRef = useRef(false);

  const submit = handleSubmit(
    async (values) => {
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;

      try {
        await onSubmit(buildSurveyAnswers(values, elements));
      } finally {
        isSubmittingRef.current = false;
      }
    },
    /**
     * Moves to the page holding the first error rather than refusing silently.
     *
     * Submit only appears on the last page, so an error anywhere earlier would
     * otherwise reject the submission while showing the respondent a page with
     * nothing wrong on it. That cannot happen through the Next button, which
     * validates as it goes, but it can when an answer on a later page changes
     * what an earlier page requires.
     */
    (submitErrors) => {
      const erroredNames = new Set(Object.keys(submitErrors));

      const erroredPage = visiblePages.find((page) =>
        page.elements.some((element) =>
          getElementFieldNames(element).some((name) => erroredNames.has(name))
        )
      );

      if (erroredPage && erroredPage.name !== currentPage?.name) {
        goToPage(erroredPage.name);
      }
    }
  );

  const renderQuestion = (element: SurveyElement) => {
    if (element.type === 'html') {
      return <SurveyHtmlBlock html={element.html ?? ''} />;
    }

    const errorMessage = errors[element.name]?.message as string | undefined;

    const otherErrorMessage = errors[getSurveyOtherFieldName(element.name)]?.message as
      | string
      | undefined;

    if (element.type === 'checkbox') {
      return (
        <SurveyCheckboxQuestion
          element={element}
          control={control}
          errorMessage={errorMessage}
          otherErrorMessage={otherErrorMessage}
          hideRequiredMarker={isPoll}
        />
      );
    }

    if (element.type === 'dropdown') {
      return (
        <SurveyDropdownQuestion
          element={element}
          control={control}
          errorMessage={errorMessage}
          otherErrorMessage={otherErrorMessage}
          hideRequiredMarker={isPoll}
        />
      );
    }

    if (element.type === 'comment') {
      return isPrimumInquiryElement(element) ? (
        <SurveyInquiryQuestion
          element={element}
          control={control}
          errors={errors}
          errorMessage={errorMessage}
        />
      ) : (
        <SurveyCommentQuestion
          element={element}
          control={control}
          errorMessage={errorMessage}
          hideRequiredMarker={isPoll}
        />
      );
    }

    if (element.type === 'radiogroup') {
      return (
        <SurveyChoiceQuestion
          element={element}
          control={control}
          errorMessage={errorMessage}
          otherErrorMessage={otherErrorMessage}
          hideRequiredMarker={isPoll}
          displayChoices={displayChoicesFor(element)}
        />
      );
    }

    return (
      <SurveyTextQuestion
        element={element}
        control={control}
        errorMessage={errorMessage}
        hideRequiredMarker={isPoll}
      />
    );
  };

  /**
   * The live-region wrapper stays mounted whether or not its question is
   * showing, because a live region only announces changes that happen inside a
   * region the screen reader was already watching — mounting the region and its
   * content together announces nothing.
   */
  const renderElement = (element: SurveyElement) => {
    if (!conditionalElementNames.has(element.name)) {
      return <Fragment key={element.name}>{renderQuestion(element)}</Fragment>;
    }

    return (
      <View key={element.name} accessibilityLiveRegion="polite">
        {hiddenElementNames.has(element.name) ? null : renderQuestion(element)}
      </View>
    );
  };

  const isStepped = visiblePages.length > 1;

  const pageElements = (currentPage?.elements ?? []).filter((element) =>
    isRecognizedElementType(element.type)
  );

  /**
   * The questions on this page that are currently blocking, named by title.
   *
   * Rendered beside the button that just refused to work rather than at the top
   * of the page, so the control that blocked explains itself without the
   * respondent being moved anywhere. A page can carry a dozen questions and
   * several errors at once, and the red text under any one of them can sit off
   * screen in either direction; one list answers "what is stopping me" however
   * many things are.
   */
  const blockingTitles = pageElements
    .filter(
      (element) =>
        !hiddenElementNames.has(element.name) &&
        getElementFieldNames(element).some((name) => errors[name])
    )
    .map((element) => element.title)
    .filter((title): title is string => !!title);

  const hasRequiredQuestion = pageElements.some(
    (element) => element.isRequired && !hiddenElementNames.has(element.name)
  );

  return (
    <VStack space="lg">
      {isStepped ? (
        <VStack space="xs">
          {/*
            Stays mounted across page changes, so changing its text is a
            mutation inside a region the screen reader was already watching.
            That is what announces the move on Android and web, where
            `announceForAccessibility` is a no-op and the iOS path above never
            runs.
          */}
          <Text
            className="text-sm text-typography-700"
            accessibilityLiveRegion="polite"
            aria-live="polite">
            {`Page ${visiblePageNumber + 1} of ${visiblePages.length}`}
          </Text>

          {/*
            The section a page belongs to, when the author named one. Until now
            this was only spoken in the iOS announcement, which left a sighted
            respondent with less context than a VoiceOver one.
          */}
          {currentPage?.title ? (
            <Text className="font-semibold text-base text-typography-900">{currentPage.title}</Text>
          ) : null}

          <Progress
            value={((visiblePageNumber + 1) / visiblePages.length) * 100}
            size="xs"
            aria-label={`Page ${visiblePageNumber + 1} of ${visiblePages.length}`}>
            <ProgressFilledTrack />
          </Progress>
        </VStack>
      ) : null}

      {/*
        Draft persistence is otherwise invisible, and a respondent who cannot
        tell whether leaving costs them their answers treats a long survey as
        all-or-nothing and puts it off. Shown only when a draft is actually
        being kept, so it never promises something a preview does not do.
        Suppressed for a poll: short enough that "saved as you go" and "*
        Required" read as noise rather than help.
      */}
      {surveyGroupId && !isPoll ? (
        <Text className="text-sm text-typography-700">
          Your answers are saved on this device as you go.
        </Text>
      ) : null}

      {hasRequiredQuestion && !isPoll ? (
        <Text className="text-sm text-typography-700">* Required</Text>
      ) : null}

      {pageElements.map(renderElement)}

      {/*
        The region is mounted whether or not anything is blocking, because a
        live region only announces changes that happen inside a region the
        screen reader was already watching — mounting the region and its
        content together announces nothing. Polite rather than assertive: the
        respondent pressed Next and is waiting on the answer, so this does not
        need to interrupt whatever is already being read.
      */}
      <View accessibilityLiveRegion="polite" aria-live="polite">
        {blockingTitles.length > 0 ? (
          <VStack space="xs" className="rounded-lg border border-error-300 bg-background-error p-3">
            <Text className="font-semibold text-typography-900">
              {blockingTitles.length === 1
                ? 'One question still needs an answer'
                : `${blockingTitles.length} questions still need an answer`}
            </Text>

            {/*
              Titles are listed only once there is more than one, because a
              single title is already shown by that question's own error and
              repeating it on a short page says the same thing twice. The
              heading stays either way, so a respondent whose one error is off
              screen still learns something is waiting.
            */}
            {blockingTitles.length > 1
              ? blockingTitles.map((title) => (
                  <Text key={title} className="text-sm text-typography-700">
                    {title}
                  </Text>
                ))
              : null}
          </VStack>
        ) : null}
      </View>

      {isStepped ? (
        <HStack space="sm" className="items-center justify-between">
          {visiblePageNumber > 0 ? (
            <Button variant="outline" action="secondary" size="md" onPress={goToPreviousPage}>
              <ButtonText>Back</ButtonText>
            </Button>
          ) : (
            <View />
          )}

          {isLastPage ? (
            <LoaderButton onPress={submit} isLoading={isSubmitting} action="primary" size="md">
              Submit
            </LoaderButton>
          ) : (
            <Button action="primary" size="md" onPress={goToNextPage}>
              <ButtonText>Next</ButtonText>
            </Button>
          )}
        </HStack>
      ) : (
        <LoaderButton onPress={submit} isLoading={isSubmitting} action="primary" size="md">
          Submit
        </LoaderButton>
      )}
    </VStack>
  );
}
